import React, { useCallback, useEffect, useState } from 'react';
import ReactSwitch from 'react-switch';
import RectangularButton from 'components/elements/molecules/RectangularButton/RectangularButton';
import DropDown from 'components/elements/atoms/DropDown/DropDown';
import DescriptionModal from './DescriptionModal';
import { getLogo } from 'helpers/sourceIcon';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as Actions from 'redux/actions/index';

const DELIVERY_DAY_OPTIONS = ['Sunday', 'Monday', 'Saturday'];
const DESCRIPTION_PREVIEW_LIMIT = 1000;

function PreferenceRow({ label, children, bordered = true }) {
  return (
    <div className={`syllabus-preference-row${bordered ? '' : ' no-border'}`}>
      <div className="syllabus-preference-label">{label}</div>
      <div className="syllabus-preference-control">{children}</div>
    </div>
  );
}

function ToggleField({ checked, onChange }) {
  return (
    <div className="syllabus-toggle-wrap">
      <ReactSwitch
        offColor="#d7dee6"
        offHandleColor="#ffffff"
        onColor="#1a73e8"
        onHandleColor="#ffffff"
        handleDiameter={16}
        width={38}
        height={22}
        borderRadius={22}
        boxShadow="0 1px 3px rgba(16, 24, 40, 0.18)"
        activeBoxShadow="0 0 0 3px rgba(26, 115, 232, 0.15)"
        checkedIcon={false}
        uncheckedIcon={false}
        checked={checked}
        onChange={onChange}
      />
    </div>
  );
}

function getSubscriptionStatusLabel(subscriptionStatus) {
  switch (subscriptionStatus) {
    case 'Free':
      return 'Free';
    case 'SchoolSubscription':
      return 'Free with school subscription';
    case 'NoSubscription':
      return 'Paid';
    default:
      return subscriptionStatus;
  }
}

function SyllabusUploadStepDeliveryPreferences({
  courseId,
  value,
  onChange,
  onBack,
  onConfirm,
  loading,
  actions,
  preferredSources,
}) {
  const [sourceOptions, setSourceOptions] = useState([]);
  const [resolvedPreferredSources, setResolvedPreferredSources] = useState(
    Array.isArray(preferredSources) ? preferredSources : [],
  );
  const [loadingSources, setLoadingSources] = useState(false);
  const [loadingPreferredSources, setLoadingPreferredSources] = useState(false);
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);

  const getSourceId = (source) =>
    source?._id || source?.sourceId || source?.id || source?.sourceName;

  const getSourceLabel = (source) => source?.sourceName || source?.name || '';

  useEffect(() => {
    const fetchSources = async () => {
      setLoadingSources(true);
      try {
        const response = await actions.getSources({
          sourceQueryText: '',
          geographicScopes: 'national,regional,local',
          location: 'All',
          pageSize: 3000,
        });
        const normalizedSources = (response?.sources || [])
          .filter((source) =>
            ['approved', 'limitedToCourse'].includes(
              source.sourceApprovalStatus,
            ),
          )
          .map((source) => ({
            label: getSourceLabel(source),
            value: getSourceId(source),
          }))
          .filter(
            (source, index, list) =>
              source.value &&
              list.findIndex((item) => item.value === source.value) === index,
          )
          .sort((a, b) => a.label.localeCompare(b.label));
        setSourceOptions(normalizedSources);
      } catch (error) {
        console.error('Error fetching sources:', error);
      } finally {
        setLoadingSources(false);
      }
    };
    fetchSources();
  }, [actions]);

  useEffect(() => {
    setResolvedPreferredSources(
      Array.isArray(preferredSources) ? preferredSources : [],
    );
  }, [preferredSources]);

  useEffect(() => {
    const fetchPreferredSources = async () => {
      if (!courseId) {
        setResolvedPreferredSources([]);
        return;
      }

      setLoadingPreferredSources(true);
      try {
        const sources = await actions.getCourseSources(courseId);
        setResolvedPreferredSources(Array.isArray(sources) ? sources : []);
      } catch (error) {
        console.error('Error fetching course sources:', error);
      } finally {
        setLoadingPreferredSources(false);
      }
    };

    fetchPreferredSources();
  }, [actions, courseId]);

  const updateValue = useCallback(
    (key, nextValue) => {
      onChange({
        ...value,
        [key]: nextValue,
      });
    },
    [onChange, value],
  );

  useEffect(() => {
    if (!sourceOptions.length || !Array.isArray(value.sources)) {
      return;
    }

    const normalizedSourceIds = value.sources
      .map((selectedValue) => {
        const byId = sourceOptions.find(
          (source) => source.value === selectedValue,
        );

        if (byId) {
          return byId.value;
        }

        const byLabel = sourceOptions.find(
          (source) => source.label === selectedValue,
        );

        return byLabel ? byLabel.value : selectedValue;
      })
      .filter(Boolean)
      .filter(
        (sourceId, index, list) =>
          list.findIndex((item) => item === sourceId) === index,
      );

    const hasChanged =
      normalizedSourceIds.length !== value.sources.length ||
      normalizedSourceIds.some(
        (sourceId, index) => sourceId !== value.sources[index],
      );

    if (hasChanged) {
      updateValue('sources', normalizedSourceIds);
    }
  }, [sourceOptions, updateValue, value.sources]);

  const mergedSourceOptions = [
    ...sourceOptions,
    ...(value.sources || []).map((sourceId) => ({
      label: sourceId,
      value: sourceId,
    })),
  ].filter(
    (source, index, list) =>
      source.value &&
      list.findIndex((item) => item.value === source.value) === index,
  );

  const selectedSourceOptions = (value.sources || []).map((sourceId) => {
    const existingOption = mergedSourceOptions.find(
      (source) => source.value === sourceId,
    );

    return (
      existingOption || {
        label: sourceId,
        value: sourceId,
      }
    );
  });

  const handleSourcesChange = (selectedOptions) => {
    updateValue(
      'sources',
      Array.isArray(selectedOptions)
        ? selectedOptions.map((option) => option.value)
        : [],
    );
  };

  const handleDescriptionSave = (newDescription) => {
    updateValue('courseDescription', newDescription.trim());
  };

  const renderPreferredSources = () => {
    if (loadingPreferredSources && !resolvedPreferredSources.length) {
      return <div className="syllabus-preference-help">Loading sources...</div>;
    }

    if (!resolvedPreferredSources.length) {
      return null;
    }

    return (
      <div className="syllabus-source-group">
        <div className="syllabus-source-group-title">Preferred Sources</div>
        <div className="syllabus-preferred-sources-list">
          {resolvedPreferredSources.map((source) => (
            <div
              className="syllabus-preferred-source-item"
              key={source._id || source.sourceName}
            >
              {source.domains?.length ? (
                <img
                  src={getLogo({ domain: source.domains[0] })}
                  width={18}
                  className="syllabus-preferred-source-logo"
                  alt={source.sourceName}
                />
              ) : (
                <div className="syllabus-preferred-source-logo-placeholder" />
              )}
              <div className="syllabus-preferred-source-copy">
                <div className="syllabus-preferred-source-name">
                  {source.sourceName}
                  {source.subscriptionStatus && (
                    <span className="syllabus-preferred-source-status">
                      {' '}
                      ({getSubscriptionStatusLabel(source.subscriptionStatus)})
                    </span>
                  )}
                </div>
                {source.notes && (
                  <div
                    className="syllabus-preferred-source-notes"
                    dangerouslySetInnerHTML={{ __html: source.notes }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="syllabus-preferred-sources-link">
          You can edit this sources{' '}
          <a
            href={`/courses/${courseId}?courseTab=course_info`}
            target="_blank"
            rel="noopener noreferrer"
          >
            here
          </a>
        </div>
      </div>
    );
  };

  return (
    <>
      <DescriptionModal
        show={descriptionModalOpen}
        onClose={() => setDescriptionModalOpen(false)}
        value={value.courseDescription || ''}
        onSave={handleDescriptionSave}
        title="Edit Course Description"
      />
      <div className="syllabus-step">
        <div className="main-con">
          <div className="modal-header">
            <div className="syllabus-step-title">
              Choose your delivery preferences
            </div>
            <p className="syllabus-step-desc">
              You can change these at any time in Settings.
            </p>
          </div>

          <div className="section-con">
            <div className="syllabus-preferences-card">
              <div className="syllabus-preference-row syllabus-preference-row-course-description">
                <div className="syllabus-preference-label">Course name</div>
                <div className="syllabus-preference-control syllabus-preference-field">
                  <input
                    type="text"
                    className="syllabus-preference-input"
                    placeholder="Enter course name"
                    value={value.courseName || ''}
                    onChange={(e) => updateValue('courseName', e.target.value)}
                  />
                  <div className="syllabus-preference-help">
                    This will help us generating relevant readings
                  </div>
                </div>
              </div>

              <div className="syllabus-preference-row syllabus-preference-row-course-description">
                <div className="syllabus-preference-label">
                  Course description
                </div>
                <div className="syllabus-preference-control syllabus-preference-field">
                  <div className="syllabus-description-field">
                    <div className="syllabus-description-preview">
                      {value.courseDescription &&
                      value.courseDescription.length > DESCRIPTION_PREVIEW_LIMIT
                        ? value.courseDescription.slice(
                            0,
                            DESCRIPTION_PREVIEW_LIMIT,
                          )
                        : value.courseDescription || 'No description'}
                    </div>
                    <div className="syllabus-description-actions">
                      {value.courseDescription &&
                        value.courseDescription.length >
                          DESCRIPTION_PREVIEW_LIMIT && (
                          <button
                            type="button"
                            className="syllabus-link-button more-link"
                            onClick={() => setDescriptionModalOpen(true)}
                          >
                            More
                          </button>
                        )}
                      <button
                        type="button"
                        className="syllabus-link-button edit-description-btn"
                        onClick={() => setDescriptionModalOpen(true)}
                      >
                        Edit description
                      </button>
                    </div>
                  </div>
                  <div className="syllabus-preference-help">
                    This will help us generating relevant readings
                  </div>
                </div>
              </div>

              <div className="syllabus-preference-row">
                <div className="syllabus-preference-label">Sources</div>
                <div className="syllabus-preference-control syllabus-preference-field">
                  <div className="syllabus-sources-sections">
                    {renderPreferredSources()}
                    <div className="syllabus-source-group">
                      <div className="syllabus-source-group-title">
                        Sources to search from
                      </div>
                      {loadingSources ? (
                        <div>Loading sources...</div>
                      ) : !mergedSourceOptions.length ? (
                        <div className="syllabus-preference-help">
                          No sources available
                        </div>
                      ) : (
                        <DropDown
                          className="syllabus-sources-dropdown"
                          value={selectedSourceOptions}
                          onChange={handleSourcesChange}
                          options={mergedSourceOptions}
                          isMulti
                        />
                      )}
                    </div>
                  </div>
                  <div className="syllabus-preference-help">
                    Select sources for generating relevant readings
                  </div>
                </div>
              </div>

              <PreferenceRow label="Delivery day">
                <select
                  className="syllabus-preference-select"
                  value={value.deliveryDay}
                  onChange={(e) => updateValue('deliveryDay', e.target.value)}
                >
                  {DELIVERY_DAY_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </PreferenceRow>

              <PreferenceRow label="Reminder if not reviewed by Tuesday">
                <ToggleField
                  checked={value.reminderIfNotReviewed}
                  onChange={(checked) =>
                    updateValue('reminderIfNotReviewed', checked)
                  }
                />
              </PreferenceRow>

              <PreferenceRow label="Show instructor notes to students">
                <ToggleField
                  checked={value.showInstructorNotes}
                  onChange={(checked) =>
                    updateValue('showInstructorNotes', checked)
                  }
                />
              </PreferenceRow>

              <PreferenceRow
                label="Auto-publish without review"
                bordered={false}
              >
                <ToggleField
                  checked={value.autoPublishWithoutReview}
                  onChange={(checked) =>
                    updateValue('autoPublishWithoutReview', checked)
                  }
                />
              </PreferenceRow>
            </div>
          </div>

          <div className="syllabus-step-footer">
            <div />
            <div className="d-flex align-items-center" style={{ gap: '1rem' }}>
              <RectangularButton onClick={onBack}>Back</RectangularButton>
              <RectangularButton
                darkButton
                disabled={loading}
                onClick={onConfirm}
              >
                {loading ? 'Finishing...' : 'Finish setup'}
              </RectangularButton>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Actions, dispatch),
  };
}

function mapStateToProps(state, ownProps) {
  return {
    preferredSources: state.courseSources[ownProps.courseId],
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SyllabusUploadStepDeliveryPreferences);
